/* eslint-disable no-underscore-dangle */
import { useMemo, useRef, useState } from 'react';
import {
	getDescopeWcDefineClass,
	getSdkVersion,
	loadSdkV2,
} from './helpers';
import { WC_NAME } from './constants';

// v1 is loaded by react-sdk
const v1Class = getDescopeWcDefineClass();

const getSdkVersionMemo = (() => {
	let prevProjectId = '';
	let prevResult = Promise.resolve('');
	return (projectId: string) => {
		if (projectId !== prevProjectId) {
			prevProjectId = projectId;
			prevResult = getSdkVersion(projectId);
		}
		return prevResult;
	};
})();

const useDynamicSdk = ({ projectId, sdkVersion }) => {
	const currentSdkVersion = useRef('');
	const [state, setState] = useState({
		projectId,
		sdkVersion: sdkVersion || currentSdkVersion.current
	});
	const classes = useRef({ v1: undefined, v2: undefined });

	useMemo(() => {
		(async () => {
			if (!classes.current.v1) {
				classes.current.v1 = await v1Class;
				const sdkV1ComponentName = `${WC_NAME}-v1`;
				if (!customElements.get(sdkV1ComponentName)) {
					// we should re-register the component under a different name so we can run flows in different versions on the same app (console-app)
					customElements.define(sdkV1ComponentName, classes.current.v1);
				}
			}

			const sdkVer = await getSdkVersionMemo(projectId);

			if (sdkVer && sdkVer !== currentSdkVersion.current) {
				currentSdkVersion.current = sdkVer;

				if (!classes.current[sdkVer]) {
					// so if we got here, it means that we need to load v2
					const v2Class = getDescopeWcDefineClass();
					await loadSdkV2();
					classes.current[sdkVer] = await v2Class;

					const sdkV2ComponentName = `${WC_NAME}-v2`;
					if (!customElements.get(sdkV2ComponentName)) {
						// we should re-register the component under a different name so we can run flows in different versions on the same app (console-app)
						customElements.define(`${WC_NAME}-v2`, classes.current.v2);
					}
				}
				if (!customElements.get(WC_NAME)) {
					class Wrapper extends classes.current[sdkVer]{}
					customElements.define(WC_NAME, Wrapper as CustomElementConstructor);
				}
			}

			setState({ projectId, sdkVersion: currentSdkVersion.current });
		})();
	}, [projectId, sdkVersion]);

	return state;
};

export default useDynamicSdk;
